import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule
} from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  filter,
  first,
  map,
  Observable,
  tap
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { routes } from './app-routing.module';
import { AuthService } from './auth/services/auth.service';
import { ChatHubConnectionService } from './communication-chat/services/chat-hub-connection.service';
import { GamesActions } from './games/store/actions/games.action';
import { NonClosableModalComponent } from './modals/components/non-closable-modal/non-closable-modal.component';
import { ProfileNotificationsActions } from './notification-center/store/actions/profileNotifications.action';
import { ENTITY_TYPES } from './permissions/models/enums/permission-names';
import { PermissionsActions, } from './permissions/store/actions/permissions.action';
import { Player } from './profile/models/currentPlayer.interface';
import {
  ProfileGeneralActions,
  ProfileTeamsActions
} from './profile/store/actions/actions-type.action';
import { selectCurrentPlayer } from './profile/store/selectors/profileGeneral.selector';
import { selectProfileTeamsSelector } from './profile/store/selectors/profileTeams.selector';
import { RewardsActions } from './reward/store/actions/reward.action';
import { FloatingSupportComponent } from './shared/components/floating-support/floating-support.component';
import { GdprPanelComponent } from './shared/components/gdpr-panel/gdpr-panel.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { PageLoaderComponent } from './shared/components/page-loader/page-loader.component';
import { CookieService } from './shared/services/cookie.service';
import { NavigationService } from './shared/services/navigation.service';
import { ProfileMainHubService } from './shared/services/profile-main-hub.service';
import { PushNotificationService } from './shared/services/push-notification.service';
import { setRefCode } from './shared/store/actions/commonSettings.action';
import { WithdrawalRequestStatusType } from './wallet/models/enums/wallet.enum';
import { WalletActions } from './wallet/store/actions/wallet.action';


@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    RouterModule,
    HeaderComponent,
    PageLoaderComponent,
    FloatingSupportComponent,
    GdprPanelComponent
  ]
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private store = inject(Store);
  private profileMainHubService = inject(ProfileMainHubService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private chatHubConnectionService = inject(ChatHubConnectionService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private cookieService = inject(CookieService);
  private dialog = inject(MatDialog);
  private pushNotification = inject(PushNotificationService);
  private destroyRef = inject(DestroyRef);

  currentPlayer$: Observable<Player> = this.store.select(selectCurrentPlayer);
  currentPlayerTeams = this.store.selectSignal(selectProfileTeamsSelector);
  currentPlayer = this.store.selectSignal(selectCurrentPlayer);

  private readonly effectRef = effect(() => {
    const player = this.currentPlayer();
    const teamsResponse = this.currentPlayerTeams();

    if (!this.authService.isAuthorised || !player.profile.playerId) return;

    this.store.dispatch(ProfileGeneralActions.fetchSubscriptionRequest({
      payload: { profileId: player.profile.playerId, query: null }
    }));

    if (!teamsResponse?.items?.length) return;

    const permissionContexts = teamsResponse.items.map(team => ({
      contextEntityType: ENTITY_TYPES.Team,
      contextEntityId: team.id
    }));

    this.store.dispatch(ProfileTeamsActions.fetchTeamsPermissionsRequest({
      payload: { userId: player.profile.userId, permissionContexts }
    }));

    this.effectRef.destroy();
  });

  constructor() {
    this.translate.setFallbackLang('en');
    this.translate.use('en');
  }

  ngOnInit(): void {
    this.checkInAppBrowser();
    this.handleRefCode();
    this.trackPageViews();
    // Initialize navigation history
    this.navigationService.startSaveHistory();
    this.handleAuthenticatedPlayer();
    this.store.dispatch(GamesActions.fetchPlatforms());
    this.initPushNotifications();
  }

  private checkInAppBrowser(): void {
    if (this.isMobile() && this.isInAppBrowser()) {
      this.dialog.open(NonClosableModalComponent, {
        panelClass: 'standard-modal-window-pane',
        data: {
          modalHeader: 'inAppBrowserWarningTitle?',
          description: 'inAppBrowserWarningDescription',
          disableClose: true,
        },
      });
      window.open('googlechrome://navigate?url=https://www.platform.lidoma.com', '_system');
    }
  }

  private handleRefCode(): void {
    this.activatedRoute.queryParams.pipe(
      filter(params => !!params.refCode),
      first(),
      switchMap((params) =>
        this.authService.isAuthenticated$.pipe(
          first(),
          map(isAuthenticated => ({ params, isAuthenticated }))
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ params, isAuthenticated }) => {
      const refCode = params.refCode;
      const now = new Date();
      if (isAuthenticated) {
        this.store.dispatch(setRefCode({
          payload: { refCode, firstAccessTimestamp: now.toISOString() }
        }));
      } else {
        this.cookieService.setCookie('refCode-lidoma', { refCode, firstAccessTimestamp: now.toISOString() }, 30);
        this.cookieService.setSimpleCookie('refCode', refCode, 30);
      }
    });
  }

  // Track page views with Google Analytics
  private trackPageViews(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: any) => {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('config', environment.googleAnalyticKey, {
          page_path: event.urlAfterRedirects
        });
      }
    });
  }

  private handleAuthenticatedPlayer(): void {
    this.authService.isAuthenticated$.pipe(
      filter(Boolean),
      tap(() => this.store.dispatch(ProfileGeneralActions.fetchCurrentPlayerData())),
      switchMap(() => this.currentPlayer$.pipe(
          first(player => !!player.profile.playerId),
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(player => {
      const { playerId, userId } = player.profile;

      this.applyRefCodeFromCookie();

      this.store.dispatch(PermissionsActions.fetchPermissionsRequests({ payload: userId }));
      this.store.dispatch(RewardsActions.claimAllPerks());
      this.store.dispatch(ProfileNotificationsActions.fetchPlayerNotifications({
        payload: { playerId, page: 1, pageSize: 10 }
      }));
      this.store.dispatch(ProfileTeamsActions.profileGetAllTeams({
        payload: { profileId: playerId }
      }));

      // Connect to hubs
      this.profileMainHubService.connect();
      this.chatHubConnectionService.connect(playerId);

      this.dispatchRoleSpecificActions(playerId);
    });
  }

  // Handle refCode from cookie
  private applyRefCodeFromCookie(): void {
    const refCodeData = this.cookieService.getCookie('refCode-lidoma');
    if (refCodeData) {
      this.store.dispatch(setRefCode({ payload: refCodeData }));
      this.cookieService.deleteCookie('refCode-lidoma');
    }
  }

  private dispatchRoleSpecificActions(playerId: string): void {
    // Role specific dispatch
    if (this.authService.isPlayer) {
      this.store.dispatch(WalletActions.fetchProfilePaymentAccounts({ payload: { playerId } }));
    }
    if (this.authService.isEmployee) {
      this.store.dispatch(WalletActions.fetchWithdrawalRequests({
        payload: { status: WithdrawalRequestStatusType.Pending }
      }));
    }
  }

  private initPushNotifications(): void {
    this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated => this.pushNotification.attachNotifications(isAuthenticated))
    ).subscribe()
  }

  isMobile(): boolean {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  isInAppBrowser(): boolean {
    const ua = (navigator as any).userAgent || (navigator as any).vendor || (window as any).opera;
    return /Instagram|Discord|Telegram|Twitch|Facebook/i.test(ua)
      || typeof (window as any).TelegramWebview !== 'undefined';
  }

  /**
   * Get available loading URLs from routes
   * @returns Array of route paths
   */
  getAvailableLoadingUrls(): (string | undefined)[] {
    return routes.map((r) => r.path);
  }
}
